// Hook: useCampaignSubmit
// Purpose: Validates and submits a campaign creation request. Handles image
// conversion/compression, FormData assembly, server submission, success/error
// modal state, and optional draft cleanup post-success.
import { useState } from 'react';
import { SERVER_URL } from '../../../api/core';
import { auth } from '../../../firebase/firebase';
import { useAuth } from '../../../context/auth';

export const useCampaignSubmit = (
  formData,
  surveyQuestions,
  selectedTheme,
  gradientColors,
  gradientDirection,
  hexText,
  previewImage,
  hasExplainerVideo,
  explainerVideo,
  clearFormCache,
  resetForm,
  namespaceId,
  // Draft cleanup parameters
  selectedDraftId = null,
  handleDeleteDraft = null
) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loadingModal, setLoadingModal] = useState({ 
    isOpen: false, 
    status: 'loading', 
    error: null, 
    campaignId: null, 
    campaignName: '', 
    videoUploadError: null,
    videoUploadWarning: null,
    shouldStayOpen: false
  });
  
  // Get the fetchUserProfile function from the auth context
  const { fetchUserProfile } = useAuth();

  // Add logging whenever loadingModal state changes
  const setLoadingModalWithLogging = (newState) => {
    console.log('=== MODAL STATE CHANGE ===');
    console.log('Previous modal state:', loadingModal);
    console.log('New modal state:', newState);
    setLoadingModal(newState);
    console.log('Modal state updated');
  };

  // Helper function to convert data URL to Blob
  const dataURLToBlob = async (dataURL) => {
    if (!dataURL) return null;
    
    try {
      if (dataURL.startsWith('data:')) {
        // For data URLs
        console.log(`Converting data URL to blob (length: ${dataURL.length} characters)`);
        const response = await fetch(dataURL);
        const blob = await response.blob();
        console.log(`Blob created from data URL: Type: ${blob.type}, Size: ${(blob.size / 1024).toFixed(2)}KB`);
        
        // If blob is still too large, force additional compression
        if (blob.size > 500 * 1024) { // Increased threshold to 500KB
          console.log('Blob is large, applying compression before upload');
          return await compressImageBlob(blob);
        }
        
        return blob;
      } else {
        // Fallback if it's just base64 content
        console.log('Using fallback base64 conversion method');
        const base64Data = dataURL.split(',')[1] || dataURL;
        const byteCharacters = atob(base64Data);
        const byteArrays = [];
        
        for (let offset = 0; offset < byteCharacters.length; offset += 512) {
          const slice = byteCharacters.slice(offset, offset + 512);
          
          const byteNumbers = new Array(slice.length);
          for (let i = 0; i < slice.length; i++) {
            byteNumbers[i] = slice.charCodeAt(i);
          }
          
          const byteArray = new Uint8Array(byteNumbers);
          byteArrays.push(byteArray);
        }
        
        const blob = new Blob(byteArrays, { type: 'image/jpeg' });
        console.log(`Blob created from base64: Type: ${blob.type}, Size: ${(blob.size / 1024).toFixed(2)}KB`);
        
        // If blob is still too large, force additional compression
        if (blob.size > 500 * 1024) { // Increased threshold to 500KB
          console.log('Blob is large, applying compression before upload');
          return await compressImageBlob(blob);
        }
        
        return blob;
      }
    } catch (error) {
      console.error('Error converting data URL to Blob:', error);
      return null;
    }
  };
  
  // Additional compression function to ensure blobs are under 200KB
  const compressImageBlob = async (blob) => {
    return new Promise((resolve, reject) => {
      try {
        console.log(`Starting emergency compression for blob: ${(blob.size / 1024).toFixed(2)}KB`);
        
        // Create blob URL and image element
        const blobUrl = URL.createObjectURL(blob);
        const img = new Image();
        
        img.onload = () => {
          // Release the blob URL
          URL.revokeObjectURL(blobUrl);
          
          // Determine dimensions to use (aim for very small)
          const MAX_DIMENSION = 1200; // Increased max dimension for better quality
          let width = img.width;
          let height = img.height;
          
          if (width > height && width > MAX_DIMENSION) {
            height = Math.round(height * (MAX_DIMENSION / width));
            width = MAX_DIMENSION;
          } else if (height > MAX_DIMENSION) {
            width = Math.round(width * (MAX_DIMENSION / height));
            height = MAX_DIMENSION;
          }
          
          console.log(`Emergency compression: Resizing from ${img.width}x${img.height} to ${width}x${height}`);
          
          // Create canvas and draw resized image
          const canvas = document.createElement('canvas');
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);
          
          // Convert to JPEG with low quality
          canvas.toBlob((resultBlob) => {
            if (!resultBlob) {
              console.error('Emergency compression failed to create blob');
              resolve(blob); // Return original as fallback
              return;
            }
            
            console.log(`Emergency compression complete: ${(resultBlob.size / 1024).toFixed(2)}KB`);
            resolve(resultBlob);
          }, 'image/jpeg', 0.8); // Force JPEG with 80% quality
        };
        
        img.onerror = (err) => {
          console.error('Emergency compression failed to load image:', err);
          URL.revokeObjectURL(blobUrl);
          resolve(blob); // Return original as fallback
        };
        
        img.src = blobUrl;
      } catch (error) {
        console.error('Error in emergency compression:', error);
        resolve(blob); // Return original as fallback
      }
    });
  };

  const handleSubmit = async () => {
    // No need to prevent default since we're not passing an event
    
    // Check if namespace ID is available
    if (!namespaceId) {
      setLoadingModalWithLogging({ 
        isOpen: true, 
        status: 'error', 
        error: 'No namespace selected. Please select a namespace before creating a campaign.' 
      });
      return;
    }
    
    setLoadingModalWithLogging({ isOpen: true, status: 'loading', error: null });
    setIsSubmitting(true);

    // Validate required fields
    const requiredFields = {
      'Campaign Name': formData.name,
      'Campaign Title': formData.title,
      'Campaign Description': formData.description
    };
    
    // Check for empty required fields
    const emptyFields = Object.entries(requiredFields)
      .filter(([_, value]) => !value || !value.trim())
      .map(([field]) => field);
    
    // Validate survey questions
    const hasValidQuestions = surveyQuestions.length > 0 && 
      surveyQuestions.some(q => q.question && q.question.trim());
    
    if (emptyFields.length > 0 || !hasValidQuestions) {
      let errorMessage = 'Please fill in the following required fields: ';
      
      if (emptyFields.length > 0) {
        errorMessage += emptyFields.join(', ');
      }
      
      if (!hasValidQuestions) {
        errorMessage += emptyFields.length > 0 ? ', and add at least one valid survey question' : 'Add at least one valid survey question';
      }
      
      setLoadingModalWithLogging({ isOpen: true, status: 'error', error: errorMessage });
      setIsSubmitting(false);
      return;
    }
    
    try {
      // Update loading modal to show campaign creation step
      setLoadingModalWithLogging(prev => ({ 
        ...prev, 
        status: 'loading', 
        message: 'Creating campaign...' 
      }));
      
      const idToken = await auth.currentUser.getIdToken();
      
      // Get custom colors if the theme is 'custom'
      const customColors = selectedTheme === 'custom' ? {
        gradientColors,
        gradientDirection,
        textColor: hexText
      } : null;
      
      // Create FormData object for multipart/form-data request
      const formDataObj = new FormData();
      
      // Add campaign data fields
      formDataObj.append('name', formData.name);
      formDataObj.append('title', formData.title);
      formDataObj.append('description', formData.description);
      formDataObj.append('category', 'other'); // Default to 'other' category
      formDataObj.append('theme', selectedTheme);
      formDataObj.append('customColors', customColors ? JSON.stringify(customColors) : '');
      
      // Extract questions and append them as individual items in an array field
      // This prevents double-JSON encoding when the server processes the data
      const questionArray = surveyQuestions.map(q => q.question);
      formDataObj.append('surveyQuestions', JSON.stringify(questionArray));
      
      formDataObj.append('hasExplainerVideo', hasExplainerVideo);
      
      // If explainer video is a YouTube URL, append it directly
      if (hasExplainerVideo && explainerVideo && typeof explainerVideo === 'string' && explainerVideo.startsWith('http')) {
        formDataObj.append('explainerVideo', explainerVideo);
      }
      
      formDataObj.append('namespaceId', namespaceId);
      
      // Add campaign image if available
      if (previewImage) {
        console.log('===== IMAGE SUBMISSION DEBUG =====');
        console.log(`Preview image type: ${previewImage.startsWith('data:') ? 'Data URL (new upload)' : 'Existing URL (from template)'}`);
        console.log(`Preview image length: ${previewImage.length} characters`);
        
        if (previewImage.startsWith('data:')) {
          // Handle new image upload (data URL)
          console.log(`Preview image MIME type: ${previewImage.split(';')[0].split(':')[1]}`);
          console.log('Converting campaign image to blob for upload');
          
          // Attempt to compress the image if needed
          const imageBlob = await dataURLToBlob(previewImage);
          
          if (imageBlob) {
            console.log(`Final image size before upload: ${(imageBlob.size / 1024).toFixed(2)}KB (${imageBlob.size} bytes)`);
            console.log(`Final image type: ${imageBlob.type}`);
            
            // Add to form data as file
            formDataObj.append('campaignImage', imageBlob, 'campaign-image.jpg');
            console.log('Campaign image blob added to form data for upload');
            
            // Final check - if still too large, alert user
            if (imageBlob.size > 250 * 1024) {
              console.warn(`WARNING: Image is still large (${(imageBlob.size / 1024).toFixed(2)}KB), might cause upload issues`);
            }
          } else {
            console.warn('Failed to convert campaign image to blob');
          }
        } else if (previewImage.startsWith('http')) {
          // Handle existing image URL (from template) - download and convert to blob
          console.log('Downloading existing image from template URL:', previewImage.substring(0, 100) + '...');
          
          try {
            // Fetch the image from the URL
            const response = await fetch(previewImage);
            if (response.ok) {
              const imageBlob = await response.blob();
              console.log(`Downloaded image size: ${(imageBlob.size / 1024).toFixed(2)}KB (${imageBlob.size} bytes)`);
              console.log(`Downloaded image type: ${imageBlob.type}`);
              
              // QUALITY FIX: Skip aggressive compression for template images to preserve quality
              // Template images are already optimized from their first upload
              const imageSizeKB = imageBlob.size / 1024;
              let finalBlob = imageBlob;
              
              if (imageSizeKB <= 512) {
                // Image is reasonably sized, use as-is to preserve quality
                console.log('Template image is already well-sized, preserving original quality');
                finalBlob = imageBlob;
              } else if (imageSizeKB <= 1024) {
                // Slightly large but acceptable, use original to preserve quality
                console.log('Template image is acceptable size, preserving quality over further compression');
                finalBlob = imageBlob;
              } else {
                // Only compress if image is very large (over 1MB)
                console.log('Template image is large, applying light compression...');
                finalBlob = await compressImageBlob(imageBlob);
              }
              
              console.log(`Final template image size: ${(finalBlob.size / 1024).toFixed(2)}KB`);
              
              // Add to form data as file (just like a new upload)
              formDataObj.append('campaignImage', finalBlob, 'campaign-image-from-template.jpg');
              console.log('Template image downloaded and added to form data for upload');
            } else {
              console.warn('Failed to download template image - HTTP status:', response.status);
            }
          } catch (downloadError) {
            console.error('Error downloading template image:', downloadError);
            console.warn('Skipping image upload due to download failure');
          }
        } else {
          console.warn('Unknown image format - not data URL or HTTP URL:', previewImage.substring(0, 50));
        }
      }
      
      console.log('===== FORM DATA SUMMARY =====');
      for (const [key, value] of formDataObj.entries()) {
        if (key === 'campaignImage') {
          console.log(`Form data contains ${key}: ${value instanceof Blob ? `${(value.size / 1024).toFixed(2)}KB ${value.type}` : 'Not a Blob'}`);
        } else {
          console.log(`Form data contains ${key}: ${typeof value === 'string' ? `${value.substring(0, 30)}${value.length > 30 ? '...' : ''}` : 'Non-string value'}`);
        }
      }
      
      // Create the campaign with the image and YouTube video URL
      console.log('Creating campaign with image and explainer video URL');
      const response = await fetch(`${SERVER_URL}/campaign/campaigns`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${idToken}`
          // Don't set Content-Type header, let the browser set it with the boundary
        },
        body: formDataObj
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: `Server error: ${response.status}` }));
        throw new Error(errorData.message || `Failed to create campaign. Server returned ${response.status}`);
      }

      const campaign = await response.json().catch(() => {
        throw new Error('Failed to parse campaign data from server');
      });
      
      console.log('Campaign created successfully:', campaign.id);
      
      // AUTO-DELETE DRAFT: If campaign was created from a draft, clean up the draft
      if (selectedDraftId && handleDeleteDraft) {
        try {
          console.log(`Auto-deleting draft ${selectedDraftId} after successful campaign creation`);
          await handleDeleteDraft(selectedDraftId);
          console.log('Draft successfully deleted after campaign creation');
        } catch (draftDeleteError) {
          console.error('Error deleting draft after campaign creation:', draftDeleteError);
          // Don't fail the campaign creation if draft deletion fails
          // Just log the error and continue
        }
      }
      
      // DON'T clear form cache here - it causes form reset and might affect modal state
      // clearFormCache(); // Moved this to when user chooses to create new campaign
      
      // DON'T reset form state - let the user see the success modal on the current step
      // The form will only reset when the user closes the modal
      
      // Show success message BEFORE refreshing profile data
      // This ensures the success modal is shown even if the user has reached their limit
      console.log('=== SETTING MODAL TO SUCCESS STATE ===');
      console.log('About to set loadingModal state to success');
      setLoadingModalWithLogging({ 
        isOpen: true, 
        status: 'success', 
        error: null,
        campaignId: campaign.id,
        campaignName: formData.title,
        shouldStayOpen: true // Keep modal open during form reset
        // Don't pass resetForm here - we'll handle it in the modal close handler
      });
      console.log('LoadingModal state set to success - modal should now be visible');
      console.log('=== MODAL STATE SET COMPLETE ===');
      
      // DON'T refresh the profile data here - it causes app re-render and resets modal state
      // The profile will be refreshed when the user navigates to campaigns list or other pages
      // setTimeout(async () => {
      //   if (auth.currentUser) {
      //     console.log('Refreshing profile data to update campaign counts');
      //     try {
      //       await fetchUserProfile(auth.currentUser, true);
      //     } catch (error) {
      //       console.error('Error refreshing profile:', error);
      //     }
      //   }
      // }, 100); // Small delay to ensure modal is displayed first
      
    } catch (err) {
      console.error('Error creating campaign:', err);
      setLoadingModalWithLogging({ isOpen: true, status: 'error', error: err.message });
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    isSubmitting,
    loadingModal,
    setLoadingModal: setLoadingModalWithLogging,
    handleSubmit
  };
}; 